import { TeamData, TeamPageData } from '@dopomogai/types';

// ===================================================================================
// 1. DATA FOR THE SUMMARY CARDS (used on /agents/teams)
// This data populates the grid of "TeamCard" components.
// ===================================================================================
export const allTeamsData: TeamData[] = [
  {
    id: 'team-content-marketing',
    slug: 'content-marketing',
    nameKey: 'teams.contentMarketing.name',
    taglineKey: 'teams.contentMarketing.tagline',
    focusAreasKeys: [
      'teams.contentMarketing.focusAreas.0',
      'teams.contentMarketing.focusAreas.1',
      'teams.contentMarketing.focusAreas.2',
      'teams.contentMarketing.focusAreas.3',
    ],
    icon: 'lightbulb',
    accentColor: 'orange',
  },
  {
    id: 'team-sales-outreach',
    slug: 'sales-outreach',
    nameKey: 'teams.salesOutreach.name',
    taglineKey: 'teams.salesOutreach.tagline',
    focusAreasKeys: [
      'teams.salesOutreach.focusAreas.0',
      'teams.salesOutreach.focusAreas.1',
      'teams.salesOutreach.focusAreas.2',
      'teams.salesOutreach.focusAreas.3',
    ],
    icon: 'target',
    accentColor: 'red',
  },
  {
    id: 'team-operations-support',
    slug: 'operations-support',
    nameKey: 'teams.operationsSupport.name',
    taglineKey: 'teams.operationsSupport.tagline',
    focusAreasKeys: [
        'teams.operationsSupport.focusAreas.0',
        'teams.operationsSupport.focusAreas.1',
        'teams.operationsSupport.focusAreas.2',
        'teams.operationsSupport.focusAreas.3',
    ],
    icon: 'settings2',
    accentColor: 'blue',
  },
  {
    id: 'team-media-localization',
    slug: 'media-localization',
    nameKey: 'teams.mediaLocalization.name',
    taglineKey: 'teams.mediaLocalization.tagline',
    focusAreasKeys: [
        'teams.mediaLocalization.focusAreas.0',
        'teams.mediaLocalization.focusAreas.1',
        'teams.mediaLocalization.focusAreas.2',
        'teams.mediaLocalization.focusAreas.3',
    ],
    icon: 'globe',
    accentColor: 'purple',
  },
  {
    id: 'team-ecommerce-growth',
    slug: 'ecommerce-growth',
    nameKey: 'teams.ecommerceGrowth.name',
    taglineKey: 'teams.ecommerceGrowth.tagline',
    focusAreasKeys: [
        'teams.ecommerceGrowth.focusAreas.0',
        'teams.ecommerceGrowth.focusAreas.1',
        'teams.ecommerceGrowth.focusAreas.2',
        'teams.ecommerceGrowth.focusAreas.3',
    ],
    icon: 'shoppingcart',
    accentColor: 'green',
  },
  {
    id: 'team-platform-development',
    slug: 'platform-development',
    nameKey: 'teams.platformDevelopment.name',
    taglineKey: 'teams.platformDevelopment.tagline',
    focusAreasKeys: [
        'teams.platformDevelopment.focusAreas.0',
        'teams.platformDevelopment.focusAreas.1',
        'teams.platformDevelopment.focusAreas.2',
        'teams.platformDevelopment.focusAreas.3',
    ],
    icon: 'code',
    accentColor: 'slate',
  },
];


// ===================================================================================
// 2. DATA FOR THE DETAIL PAGES (used on /agents/team/[slug])
// This object holds the rich content for each team's dedicated page.
// The keys match the 'slug' from the allTeamsData array.
// ===================================================================================
const teamDetailsContent: Record<string, Omit<TeamPageData, keyof TeamData>> = {
  'content-marketing': {
    hero: {
      headlineKey: 'teams.contentMarketing.detail.hero.headline',
      descriptionKey: 'teams.contentMarketing.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1740',
    },
    featuredAgentIds: ['retailer-ai-123'],
    featuredSolutionIds: [],
    strategy: {
      titleKey: 'teams.contentMarketing.detail.strategy.title',
      descriptionKey: 'teams.contentMarketing.detail.strategy.description',
      principles: [
        { titleKey: 'teams.contentMarketing.detail.strategy.principles.0.title', descriptionKey: 'teams.contentMarketing.detail.strategy.principles.0.description', icon: 'barchart' },
        { titleKey: 'teams.contentMarketing.detail.strategy.principles.1.title', descriptionKey: 'teams.contentMarketing.detail.strategy.principles.1.description', icon: 'users' },
        { titleKey: 'teams.contentMarketing.detail.strategy.principles.2.title', descriptionKey: 'teams.contentMarketing.detail.strategy.principles.2.description', icon: 'zap' }
      ]
    },
    impact: {
      titleKey: 'teams.contentMarketing.detail.impact.title',
      metrics: [
        { valueKey: 'teams.contentMarketing.detail.impact.metrics.0.value', labelKey: 'teams.contentMarketing.detail.impact.metrics.0.label', icon: 'trendingup' },
        { valueKey: 'teams.contentMarketing.detail.impact.metrics.1.value', labelKey: 'teams.contentMarketing.detail.impact.metrics.1.label', icon: 'thumbsup' },
        { valueKey: 'teams.contentMarketing.detail.impact.metrics.2.value', labelKey: 'teams.contentMarketing.detail.impact.metrics.2.label', icon: 'clock' }
      ]
    },
    cta: {
      titleKey: 'teams.contentMarketing.detail.cta.title',
      buttonKey: 'teams.contentMarketing.detail.cta.button'
    }
  },
  'sales-outreach': {
    hero: {
      headlineKey: 'teams.salesOutreach.detail.hero.headline',
      descriptionKey: 'teams.salesOutreach.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?q=80&w=1740',
    },
    featuredAgentIds: [],
    featuredSolutionIds: [],
    strategy: {
      titleKey: 'teams.salesOutreach.detail.strategy.title',
      descriptionKey: 'teams.salesOutreach.detail.strategy.description',
      principles: [
        { titleKey: 'teams.salesOutreach.detail.strategy.principles.0.title', descriptionKey: 'teams.salesOutreach.detail.strategy.principles.0.description', icon: 'filter' },
        { titleKey: 'teams.salesOutreach.detail.strategy.principles.1.title', descriptionKey: 'teams.salesOutreach.detail.strategy.principles.1.description', icon: 'message' },
        { titleKey: 'teams.salesOutreach.detail.strategy.principles.2.title', descriptionKey: 'teams.salesOutreach.detail.strategy.principles.2.description', icon: 'database' }
      ]
    },
     impact: {
      titleKey: 'teams.salesOutreach.detail.impact.title',
      metrics: [
        { valueKey: 'teams.salesOutreach.detail.impact.metrics.0.value', labelKey: 'teams.salesOutreach.detail.impact.metrics.0.label', icon: 'users' },
        { valueKey: 'teams.salesOutreach.detail.impact.metrics.1.value', labelKey: 'teams.salesOutreach.detail.impact.metrics.1.label', icon: 'clock' },
        { valueKey: 'teams.salesOutreach.detail.impact.metrics.2.value', labelKey: 'teams.salesOutreach.detail.impact.metrics.2.label', icon: 'checkcircle' }
      ]
    },
    cta: {
      titleKey: 'teams.salesOutreach.detail.cta.title',
      buttonKey: 'teams.salesOutreach.detail.cta.button'
    }
  },
  'operations-support': {
    hero: {
      headlineKey: 'teams.operationsSupport.detail.hero.headline',
      descriptionKey: 'teams.operationsSupport.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1740',
    },
    featuredAgentIds: [],
    featuredSolutionIds: [],
    strategy: {
      titleKey: 'teams.operationsSupport.detail.strategy.title',
      descriptionKey: 'teams.operationsSupport.detail.strategy.description',
      principles: [
        { titleKey: 'teams.operationsSupport.detail.strategy.principles.0.title', descriptionKey: 'teams.operationsSupport.detail.strategy.principles.0.description', icon: 'search' },
        { titleKey: 'teams.operationsSupport.detail.strategy.principles.1.title', descriptionKey: 'teams.operationsSupport.detail.strategy.principles.1.description', icon: 'book' },
        { titleKey: 'teams.operationsSupport.detail.strategy.principles.2.title', descriptionKey: 'teams.operationsSupport.detail.strategy.principles.2.description', icon: 'send' }
      ]
    },
    impact: {
      titleKey: 'teams.operationsSupport.detail.impact.title',
      metrics: [
        { valueKey: 'teams.operationsSupport.detail.impact.metrics.0.value', labelKey: 'teams.operationsSupport.detail.impact.metrics.0.label', icon: 'zap' },
        { valueKey: 'teams.operationsSupport.detail.impact.metrics.1.value', labelKey: 'teams.operationsSupport.detail.impact.metrics.1.label', icon: 'briefcase' },
        { valueKey: 'teams.operationsSupport.detail.impact.metrics.2.value', labelKey: 'teams.operationsSupport.detail.impact.metrics.2.label', icon: 'shieldcheck' }
      ]
    },
    cta: {
      titleKey: 'teams.operationsSupport.detail.cta.title',
      buttonKey: 'teams.operationsSupport.detail.cta.button'
    }
  },
  'media-localization': {
    hero: {
      headlineKey: 'teams.mediaLocalization.detail.hero.headline',
      descriptionKey: 'teams.mediaLocalization.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1505664194779-8be224a42a4a?q=80&w=1740',
    },
    featuredAgentIds: [],
    featuredSolutionIds: ['globalvoice-ai-dubbing-studio'],
    strategy: {
      titleKey: 'teams.mediaLocalization.detail.strategy.title',
      descriptionKey: 'teams.mediaLocalization.detail.strategy.description',
      principles: [
        { titleKey: 'teams.mediaLocalization.detail.strategy.principles.0.title', descriptionKey: 'teams.mediaLocalization.detail.strategy.principles.0.description', icon: 'filetext' },
        { titleKey: 'teams.mediaLocalization.detail.strategy.principles.1.title', descriptionKey: 'teams.mediaLocalization.detail.strategy.principles.1.description', icon: 'globe' },
        { titleKey: 'teams.mediaLocalization.detail.strategy.principles.2.title', descriptionKey: 'teams.mediaLocalization.detail.strategy.principles.2.description', icon: 'smile' }
      ]
    },
    impact: {
      titleKey: 'teams.mediaLocalization.detail.impact.title',
      metrics: [
        { valueKey: 'teams.mediaLocalization.detail.impact.metrics.0.value', labelKey: 'teams.mediaLocalization.detail.impact.metrics.0.label', icon: 'dollar' },
        { valueKey: 'teams.mediaLocalization.detail.impact.metrics.1.value', labelKey: 'teams.mediaLocalization.detail.impact.metrics.1.label', icon: 'clock' },
        { valueKey: 'teams.mediaLocalization.detail.impact.metrics.2.value', labelKey: 'teams.mediaLocalization.detail.impact.metrics.2.label', icon: 'activity' }
      ]
    },
    cta: {
      titleKey: 'teams.mediaLocalization.detail.cta.title',
      buttonKey: 'teams.mediaLocalization.detail.cta.button'
    }
  },
  'ecommerce-growth': {
    hero: {
      headlineKey: 'teams.ecommerceGrowth.detail.hero.headline',
      descriptionKey: 'teams.ecommerceGrowth.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1522204523234-8729aa6e3d54?q=80&w=1740',
    },
    featuredAgentIds: ['retailer-ai-123'],
    featuredSolutionIds: [],
    strategy: {
      titleKey: 'teams.ecommerceGrowth.detail.strategy.title',
      descriptionKey: 'teams.ecommerceGrowth.detail.strategy.description',
      principles: [
        { titleKey: 'teams.ecommerceGrowth.detail.strategy.principles.0.title', descriptionKey: 'teams.ecommerceGrowth.detail.strategy.principles.0.description', icon: 'usercheck' },
        { titleKey: 'teams.ecommerceGrowth.detail.strategy.principles.1.title', descriptionKey: 'teams.ecommerceGrowth.detail.strategy.principles.1.description', icon: 'trendingup' },
        { titleKey: 'teams.ecommerceGrowth.detail.strategy.principles.2.title', descriptionKey: 'teams.ecommerceGrowth.detail.strategy.principles.2.description', icon: 'messagecircle' }
      ]
    },
    impact: {
      titleKey: 'teams.ecommerceGrowth.detail.impact.title',
      metrics: [
        { valueKey: 'teams.ecommerceGrowth.detail.impact.metrics.0.value', labelKey: 'teams.ecommerceGrowth.detail.impact.metrics.0.label', icon: 'dollar' },
        { valueKey: 'teams.ecommerceGrowth.detail.impact.metrics.1.value', labelKey: 'teams.ecommerceGrowth.detail.impact.metrics.1.label', icon: 'zap' },
        { valueKey: 'teams.ecommerceGrowth.detail.impact.metrics.2.value', labelKey: 'teams.ecommerceGrowth.detail.impact.metrics.2.label', icon: 'shoppingcart' }
      ]
    },
    cta: {
      titleKey: 'teams.ecommerceGrowth.detail.cta.title',
      buttonKey: 'teams.ecommerceGrowth.detail.cta.button'
    }
  },
  'platform-development': {
    hero: {
      headlineKey: 'teams.platformDevelopment.detail.hero.headline',
      descriptionKey: 'teams.platformDevelopment.detail.hero.description',
      visualUrl: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=1740',
    },
    featuredAgentIds: [],
    featuredSolutionIds: [],
    strategy: {
      titleKey: 'teams.platformDevelopment.detail.strategy.title',
      descriptionKey: 'teams.platformDevelopment.detail.strategy.description',
      principles: [
        { titleKey: 'teams.platformDevelopment.detail.strategy.principles.0.title', descriptionKey: 'teams.platformDevelopment.detail.strategy.principles.0.description', icon: 'code' },
        { titleKey: 'teams.platformDevelopment.detail.strategy.principles.1.title', descriptionKey: 'teams.platformDevelopment.detail.strategy.principles.1.description', icon: 'puzzle' },
        { titleKey: 'teams.platformDevelopment.detail.strategy.principles.2.title', descriptionKey: 'teams.platformDevelopment.detail.strategy.principles.2.description', icon: 'shield' }
      ]
    },
    impact: {
      titleKey: 'teams.platformDevelopment.detail.impact.title',
      metrics: [
        { valueKey: 'teams.platformDevelopment.detail.impact.metrics.0.value', labelKey: 'teams.platformDevelopment.detail.impact.metrics.0.label', icon: 'rocket' },
        { valueKey: 'teams.platformDevelopment.detail.impact.metrics.1.value', labelKey: 'teams.platformDevelopment.detail.impact.metrics.1.label', icon: 'zap' },
        { valueKey: 'teams.platformDevelopment.detail.impact.metrics.2.value', labelKey: 'teams.platformDevelopment.detail.impact.metrics.2.label', icon: 'award' }
      ]
    },
    cta: {
      titleKey: 'teams.platformDevelopment.detail.cta.title',
      buttonKey: 'teams.platformDevelopment.detail.cta.button'
    }
  },
};


// ===================================================================================
// 3. DATA-LOADING FUNCTIONS
// These functions are used by the application to fetch team data.
// ===================================================================================

/**
 * Loads all team summary data for the main showcase grid.
 * @returns A promise that resolves to an array of all team data.
 */
export const loadAllTeams = async (): Promise<TeamData[]> => {
  // Simulate network delay for realistic loading
  await new Promise(resolve => setTimeout(resolve, 100));
  return allTeamsData;
};

/**
 * Loads the complete data set for a single team detail page by its slug.
 * It combines the base data with the detailed content.
 * @param slug The URL slug of the team.
 * @returns A promise resolving to the full TeamPageData or null if not found.
 */
export const loadTeamPageDataBySlug = async (slug: string): Promise<TeamPageData | null> => {
  console.log(`Loading detailed data for team: ${slug}`);
  const baseTeamData = allTeamsData.find(t => t.slug === slug);
  const detailContent = teamDetailsContent[slug];

  if (!baseTeamData || !detailContent) {
    console.error(`Data not found for team slug: ${slug}`);
    return null;
  }

  // Combine the base data with the detailed content into a single object
  const fullTeamPageData: TeamPageData = {
    ...baseTeamData,
    hero: detailContent.hero,
    featuredAgentIds: detailContent.featuredAgentIds,
    featuredSolutionIds: detailContent.featuredSolutionIds,
    strategy: detailContent.strategy,
    impact: detailContent.impact,
    cta: detailContent.cta,
  };

  await new Promise(resolve => setTimeout(resolve, 50)); // Simulate network delay
  return fullTeamPageData;
};